import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AppTokens } from './components/AppTokens';
import { ProductsManager } from './components/CsvManager';
import { General } from './components/General';
import { HubSpot } from './components/HubSpot';
import { ImagesManager } from './components/ImagesManager';
import { Meta } from './components/Meta';
import { Omnichat } from './components/Omnichat';
import { Shopify } from './components/Shopify';
import { User } from './components/User';
import { Vtex } from './components/Vtex';

import { withUser } from '@/hocs/withUser';
import { cn } from '@/lib/utils';
import { Platform } from '@/services/CustomerService';

export const Settings = withUser(({ user }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'pages.Settings' });

  const [searchParams, setSearchParams] = useSearchParams();

  const menuFromUrl = searchParams.get('menu') ?? 'general';

  const menuItems = [
    {
      value: 'general',
      label: t('menu.generalLabel'),
      active: true,
      Component: General,
    },
    {
      value: 'products-manager',
      label: t('menu.productsManagerLabel'),
      active: true,
      Component: ProductsManager,
    },
    {
      value: 'images-manager',
      label: t('menu.imagesManagerLabel'),
      active: true,
      Component: ImagesManager,
    },
    {
      value: 'shopify',
      label: t('menu.shopifyLabel'),
      active: user?.customer.platform === Platform.SHOPIFY,
      Component: Shopify,
    },
    {
      value: 'app-tokens',
      label: t('menu.appTokensLabel'),
      active: true,
      Component: AppTokens,
    },
    {
      value: 'vtex',
      label: t('menu.vtexLabel'),
      active: user?.customer.platform === Platform.VTEX,
      Component: Vtex,
    },
    {
      value: 'meta',
      label: t('menu.metaLabel'),
      active: true,
      Component: Meta,
    },
    {
      value: 'hubspot',
      label: t('menu.hubspotLabel'),
      active: true,
      Component: HubSpot,
    },
    {
      value: 'omnichat',
      label: t('menu.omnichatLabel'),
      active: true,
      Component: Omnichat,
    },
  ];

  const activeMenuItems = menuItems.filter((item) => item.active);
  const activeMenu = menuItems.find((item) => item.value === menuFromUrl);

  function handleChangeMenu(value: string) {
    searchParams.set('menu', value);
    setSearchParams(searchParams);
  }

  useEffect(() => {
    const isMenuActive = activeMenuItems.find((t) => t.value === menuFromUrl);

    if (!isMenuActive) {
      searchParams.set('menu', 'general');
      setSearchParams(searchParams);
    }
  }, [activeMenuItems, menuFromUrl, searchParams, setSearchParams]);

  return (
    <div className="py-8">
      <section className="mb-10">
        <h1 className="text-[42px] tracking-[8.25px]">{t('title')}</h1>
      </section>

      <section>
        <User />
      </section>

      <div className="flex gap-4">
        <aside className="relative w-64">
          <nav className="space-y-1">
            {activeMenuItems.map((item) => (
              <button
                key={item.value}
                onClick={() => handleChangeMenu(item.value)}
                className={cn(
                  'flex w-full items-center justify-start rounded-lg border border-transparent px-4 py-3 text-left text-sm font-medium uppercase tracking-wide transition-colors',
                  menuFromUrl === item.value
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background hover:bg-muted',
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <section className="flex-1">
          {activeMenu && <activeMenu.Component />}
        </section>
      </div>
    </div>
  );
});
